<template>
  <div class="member-verification-page">
    <div class="verification-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin loading-icon"></i>
        <p>Verifying your membership...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-circle error-icon"></i>
        <h2>Verification Failed</h2>
        <p class="error-message">{{ error }}</p>
        <router-link to="/register-scav" class="btn btn-primary">
          <i class="fas fa-envelope"></i> Request New Verification Email
        </router-link>
      </div>

      <!-- Account Already Created -->
      <div v-else-if="accountCreated" class="success-state">
        <i class="fas fa-party-horn success-icon"></i>
        <h2>Welcome to SCAV Member Portal!</h2>
        <p class="success-message">Your account has been created successfully.</p>
        
        <div class="welcome-card">
          <h3>You're all set, {{ firstName }}!</h3>
          <p>You can now access all SCAV member resources and features.</p>
        </div>
        
        <button @click="goToDashboard" class="btn btn-primary btn-large">
          <i class="fas fa-home"></i> Go to Dashboard
        </button>
      </div>

      <!-- Set Password Form -->
      <div v-else class="set-password-section">
        <div class="verification-success-header">
          <i class="fas fa-check-circle success-icon"></i>
          <h2>Membership Verified!</h2>
        </div>
        
        <div class="member-card">
          <h3>{{ firstName }} {{ lastName }}</h3>
          <div class="member-details">
            <p>
              <strong>Email:</strong> 
              <span>{{ email }}</span>
            </p>
            <p>
              <strong>Member Status:</strong> 
              <span class="badge badge-success">
                <i class="fas fa-check-circle"></i> {{ memberStatus }}
              </span>
            </p>
            <p>
              <strong>Profile ID:</strong> 
              <span>{{ profileId }}</span>
            </p>
          </div>
        </div>

        <form @submit.prevent="createAccount" class="password-form">
          <h3 class="form-title">Set Your Password</h3>
          
          <div class="form-group">
            <label for="password">Password</label>
            <div class="password-input-wrapper">
              <input 
                id="password"
                v-model="password" 
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                minlength="8"
                placeholder="At least 8 characters"
                required
                :disabled="submitting"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="passwordConfirm">Confirm Password</label>
            <div class="password-input-wrapper">
              <input 
                id="passwordConfirm"
                v-model="passwordConfirm" 
                :type="showPasswordConfirm ? 'text' : 'password'"
                class="form-control"
                minlength="8"
                placeholder="Re-enter your password"
                required
                :disabled="submitting"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPasswordConfirm = !showPasswordConfirm"
                tabindex="-1"
              >
                <i :class="showPasswordConfirm ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <div v-if="passwordError" class="alert alert-danger">
            <i class="fas fa-exclamation-triangle"></i>
            {{ passwordError }}
          </div>

          <button 
            type="submit" 
            class="btn btn-primary btn-block" 
            :disabled="submitting"
          >
            <span v-if="submitting">
              <i class="fas fa-spinner fa-spin"></i> Creating account...
            </span>
            <span v-else>
              <i class="fas fa-user-plus"></i> Create Account
            </span>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import auth from '@/services/auth'

export default {
  name: 'VerifyMember',
  data() {
    return {
      token: null,
      loading: true,
      error: null,
      email: '',
      firstName: '',
      lastName: '',
      memberStatus: '',
      profileId: '',
      password: '',
      passwordConfirm: '',
      passwordError: null,
      submitting: false,
      accountCreated: false,
      showPassword: false,
      showPasswordConfirm: false
    }
  },
  async mounted() {
    this.token = this.$route.params.token
    await this.loadVerification()
  },
  methods: {
    async loadVerification() {
      try {
        const response = await api.get(`/users/verify-member/${this.token}/`)
        
        if (response.data.valid) {
          this.email = response.data.email
          this.firstName = response.data.first_name
          this.lastName = response.data.last_name
          this.memberStatus = response.data.member_status
          this.profileId = response.data.profile_id
        }
        
      } catch (error) {
        if (error.response?.data) {
          this.error = error.response.data.message || error.response.data.error
        } else {
          this.error = 'Invalid or expired verification link. Please request a new one.'
        }
      } finally {
        this.loading = false
      }
    },
    
    async createAccount() {
      this.passwordError = null
      
      // Validation
      if (this.password.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }
      
      if (this.password !== this.passwordConfirm) {
        this.passwordError = 'Passwords do not match'
        return
      }
      
      this.submitting = true
      
      try {
        const response = await api.post(`/users/verify-member/${this.token}/`, {
          password: this.password
        })
        
        if (response.data.success) {
          // Save auth tokens
          if (response.data.tokens) {
            auth.setTokens(response.data.tokens)
            auth.setUser(response.data.user)
          }
          
          this.accountCreated = true
        }
        
      } catch (error) {
        this.passwordError = error.response?.data?.error || 'Failed to create account. Please try again.'
      } finally {
        this.submitting = false
      }
    },
    
    goToDashboard() {
      this.$router.push('/compliance-health')
    }
  }
}
</script>

<style scoped>
.member-verification-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.verification-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 40px;
  max-width: 550px;
  width: 100%;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 40px 20px;
}

.loading-icon {
  font-size: 48px;
  color: #667eea;
  margin-bottom: 16px;
}

.loading-state p {
  color: #718096;
  font-size: 16px;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 20px;
}

.error-icon {
  font-size: 64px;
  color: #f56565;
  margin-bottom: 16px;
}

.error-state h2 {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.error-message {
  color: #718096;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 20px;
}

.success-icon {
  font-size: 64px;
  color: #48bb78;
  margin-bottom: 16px;
}

.success-state h2 {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.success-message {
  color: #718096;
  margin-bottom: 24px;
}

.welcome-card {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 24px;
  margin: 24px 0;
  text-align: left;
}

.welcome-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
}

.welcome-card p {
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

/* Set Password Section */
.set-password-section {
  padding: 20px 0;
}

.verification-success-header {
  text-align: center;
  margin-bottom: 24px;
}

.verification-success-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
  margin-top: 12px;
}

.member-card {
  background: #f7fafc;
  border: 2px solid #48bb78;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
}

.member-card h3 {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 16px;
}

.member-details p {
  margin: 12px 0;
  color: #4a5568;
}

.member-details strong {
  display: inline-block;
  min-width: 120px;
  color: #2d3748;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.badge-success {
  background: #c6f6d5;
  color: #22543d;
}

/* Password Form */
.password-form {
  margin-top: 24px;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 20px;
  text-align: center;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 8px;
}

.password-input-wrapper {
  position: relative;
}

.form-control {
  width: 100%;
  padding: 12px;
  padding-right: 45px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f7fafc;
  cursor: not-allowed;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #667eea;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-block {
  width: 100%;
  margin-top: 16px;
}

.btn-large {
  padding: 16px 32px;
  font-size: 16px;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-danger {
  background: #fed7d7;
  color: #c53030;
  border: 1px solid #fc8181;
}

@media (max-width: 576px) {
  .verification-container {
    padding: 24px;
  }
  
  .member-card {
    padding: 16px;
  }
  
  .member-details strong {
    min-width: 100px;
    font-size: 14px;
  }
}
</style>

